import { authFetch } from './authFetch.js';
import { showBillingDisclosure } from '../ui/BillingDisclosureModal.js';
import { openExternalUrl, initNativeUrlOpenListener } from './externalUrl.js';
import { canPurchaseInApp, isNativeApp, purchaseChannel } from './purchase.js';
import { showPlayOnlyModal } from '../ui/PlayOnlyModal.js';
import { playBillingService } from './PlayBillingService.js';


/**
 * SubscriptionService
 * Handles Stripe subscription flow and status checks
 */
export class SubscriptionService {
    constructor() {
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
        this.priceId = import.meta.env.VITE_STRIPE_PRICE_ID || '';
        this.cacheKey = 'zephyr_subscription';
        this.cacheTtl = 5 * 60 * 1000; // 5 minutes

        initNativeUrlOpenListener();
    }

    /**
     * Redirect user to Stripe checkout. Gated behind showBillingDisclosure()
     * here -- not at each call site -- so every entry point (Saved Routes,
     * Campground Filters, the departure slider, the main Subscribe button)
     * gets the Play Store Alternative Billing disclosure automatically,
     * with no risk of a future upgrade prompt skipping it by accident.
     *
     * openExternalUrl() (not a plain redirect) because Stripe's checkout
     * domain was never in capacitor.config.json's allowNavigation --
     * Capacitor's own default then bounces it to the external system
     * browser on native, which has separate storage from the app's own
     * WebView, so the success_url return never reached the app (confirmed:
     * "it still says subscribe" after a real completed checkout). See
     * externalUrl.js for the fix -- same root cause as Google sign-in.
     */
    async startCheckout(userId, userEmail) {
        if (!canPurchaseInApp()) return;
        // Australians buy through Google Play in the Android app, never Stripe.
        if (purchaseChannel() === 'play-link') { showPlayOnlyModal(); return; }
        // Inside the Android app Pro is bought through Google Play.
        if (isNativeApp()) {
            try {
                if (await playBillingService.purchase(userId)) {
                    this.clearCache();
                    window.location.reload();
                }
            } catch (error) {
                console.error('Google Play purchase error:', error);
                alert('Google Play purchase did not complete. Please try again.');
            }
            return;
        }
        const consented = await showBillingDisclosure();
        if (!consented) return;

        try {
            const response = await authFetch(`${this.backendUrl}/api/stripe-checkout`, {
                method: 'POST',
                body: JSON.stringify({ userId, userEmail })
            });

            const data = await response.json();

            if (data.url) {
                await openExternalUrl(data.url);
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Failed to start checkout. Please try again.');
        }
    }

    /**
     * Check if user has an active subscription
     * Caches result for 5 minutes to avoid hammering the API
     */
    async isSubscribed(userId) {
        if (!userId) return false;

        // Check cache first
        const cached = this._getCached(userId);
        if (cached !== null) return cached;

        try {
            const response = await authFetch(
                `${this.backendUrl}/api/subscription-status?userId=${userId}`
            );
            const data = await response.json();
            const isActive = data.isActive === true;

            // Cache the result
            this._setCache(userId, isActive);
            return isActive;
        } catch (error) {
            console.error('Subscription check error:', error);
            return false;
        }
    }

    /**
     * Clear subscription cache (call after successful checkout)
     */
    clearCache() {
        localStorage.removeItem(this.cacheKey);
    }

    _getCached(userId) {
        try {
            const raw = localStorage.getItem(this.cacheKey);
            if (!raw) return null;
            const { uid, value, expires } = JSON.parse(raw);
            if (uid !== userId || Date.now() > expires) return null;
            return value;
        } catch {
            return null;
        }
    }

    _setCache(userId, value) {
        localStorage.setItem(this.cacheKey, JSON.stringify({
            uid: userId,
            value,
            expires: Date.now() + this.cacheTtl
        }));
    }
}
